import { IonRouterOutlet } from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { useEffect } from 'react'
import { Redirect, Route } from 'react-router-dom'
import { parseShortLinkSlug } from '../helpers/shortLinkSlug'
import { useToast } from '../hooks/useToast'
import { DownloadAppPage } from '../pages/DownloadAppPage'
import { EnterPhonePage } from '../pages/auth/EnterPhonePage'
import { LogoutPage } from '../pages/auth/LogoutPage'
import { ValidateCodePage } from '../pages/auth/ValidateCodePage'

const LOGIN_TOAST_DURATION_MS = 3000
// One toast at a time: suppress re-mounts (StrictMode double-effects, rapid
// redirects) while the previous toast is still on screen
const LOGIN_TOAST_DEDUPE_MS = LOGIN_TOAST_DURATION_MS
let loginToastShownAt = 0

// Test-only: the 3s window must not leak across vitest tests
export const resetLoginToastDedupe = () => {
  loginToastShownAt = 0
}

/** Protected path hit while logged out → login, with v1's heads-up toast. */
const RedirectToLogin = () => {
  const { _ } = useLingui()
  const [presentToast] = useToast()

  useEffect(() => {
    const now = Date.now()
    if (now - loginToastShownAt < LOGIN_TOAST_DEDUPE_MS) return
    loginToastShownAt = now
    presentToast({
      message: _(msg`Please login to continue`),
      position: 'top',
      duration: LOGIN_TOAST_DURATION_MS,
      color: 'medium',
      cssClass: 'login-toast',
    })
  }, [presentToast, _])

  return <Redirect to='/login' />
}

export const AuthRouter = () => (
  <IonRouterOutlet>
    {
      // The /:404 completely ridiculous workaround to the terrible ionic bug
      // https://github.com/ionic-team/ionic-framework/issues/23743
      //
      // A fallback route corrupts future routing state by making match.params empty
      // So by specifying a catchall slug, its then an "explicitly" matched route
      <Route
        path='/:404'
        render={({ location }) => {
          // A bare SMS slug tapped while logged out goes to the public
          // short-link mini-app, not to login
          const slug = parseShortLinkSlug(location.pathname)
          if (slug)
            return (
              <Redirect to={`/o/${slug}${location.search}${location.hash}`} />
            )
          return <RedirectToLogin />
        }}
      />
    }
    <Route exact path='/login' render={() => <EnterPhonePage />} />
    <Route exact path='/validate' render={() => <ValidateCodePage />} />
    <Route exact path='/logout' render={() => <LogoutPage />} />
    {/* Store redirect must work logged-out too (shared install links) */}
    <Route exact path='/download' render={() => <DownloadAppPage />} />
    <Route exact path='/' render={() => <Redirect to='/login' />} />
  </IonRouterOutlet>
)
